import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Form, Formik } from "formik";
import { ReactElement } from "react";
import * as Yup from "yup";

import { createOrEditResponseScriptGroupInputSchema } from "@stw/yup-validators";

import {
  EditResponseScriptGroupInput,
  ResponseScriptGroup,
} from "~frontend/__generated__/graphql";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import FormikSwitchField from "~frontend/components/forms/FormikSwitchField";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { useErrorMessageFormatter } from "~frontend/util/errors";

export default function EditGroupDialog({
  open,
  valuesToEdit,
  onClose,
  onSubmit,
  allowSettingSubaccountDefault,
}: {
  open: boolean;
  valuesToEdit: ResponseScriptGroup;
  onClose: () => void;
  onSubmit: (values: EditResponseScriptGroupInput) => Promise<void>;
  allowSettingSubaccountDefault: boolean;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();

  return (
    <Dialog open={open}>
      <DialogTitle>Edit Group</DialogTitle>
      <Formik
        initialValues={{
          [submitErrorField]: null,
          title: valuesToEdit.title,
          includeInCampaignByDefault: valuesToEdit.includeInCampaignByDefault,
        }}
        validationSchema={
          createOrEditResponseScriptGroupInputSchema(Yup).fields.input
        }
        onSubmit={async (values, formik) => {
          try {
            await onSubmit({
              title: values.title,
              includeInCampaignByDefault: values.includeInCampaignByDefault,
              groupId: valuesToEdit.id,
            });
            formik.resetForm();
          } catch (e) {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(e),
            });
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent sx={{ minWidth: "400px" }}>
              <FormikErrorBanner />
              <FormikTextField
                name="title"
                label="Title"
                fullWidth
                inputProps={{
                  "data-testid": "edit-script-response-group-title-field",
                }}
              />
              {allowSettingSubaccountDefault && (
                <FormikSwitchField
                  name="includeInCampaignByDefault"
                  label="Include this group and its responses in new campaigns by default"
                />
              )}
            </DialogContent>

            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="secondary"
              >
                Cancel
              </Button>

              <FormikSubmitButton
                color="primary"
                variant="contained"
                submitText="Update"
                loadingText="Update..."
                data-testid="update-script-response-group-button"
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
